import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { addDoc, collection, collectionData, collectionGroup, deleteDoc, doc, Firestore, orderBy, query, serverTimestamp, updateDoc, where } from '@angular/fire/firestore'
import { Auth } from '@angular/fire/auth'
import { CommentWithId, Fresh, SegmentationCommentWithId, SegmentationComment, VolumeComment, VolumeCommentWithId, Reply, CommentAction, LesionComment } from '@innersight-os/shared/firestore-interfaces'
import { DocumentData } from '@firebase/firestore-types'

// type CommentWithId = VolumeCommentWithId | SegmentationCommentWithId

const createId = (db: Firestore) => doc(collection(db, 'dummy')).id

@Injectable({
  providedIn: 'root'
})
export class CommentsService {
  constructor(
    private auth: Auth,
    private db: Firestore,
  ) {}

  getVolumeComments = (operationId: string, uploadId: string, volumeId: string): Observable<VolumeCommentWithId[]> =>
    collectionData(
      query<VolumeCommentWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collection(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/comments`) as any,
        orderBy('createdAt', 'desc')
      ),
      {idField: 'id'},
    ) as Observable<VolumeCommentWithId[]>

  getCommentsForOperation = (operationId: string): Observable<CommentWithId[]> =>
    collectionData(
      query<CommentWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collectionGroup(this.db, 'comments') as any,
        where('operationId', '==', operationId),
      ),
      {idField: 'id'},
    ) as Observable<CommentWithId[]>

  getSegmentationComments = (operationId: string, uploadId: string, volumeId: string, segmentationId: string): Observable<SegmentationCommentWithId[]> =>
    collectionData(
      query<SegmentationCommentWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collection(this.db, `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations/${segmentationId}/comments`) as any,
        orderBy('createdAt', 'desc')
      ),
      {idField: 'id'},
    ) as Observable<SegmentationCommentWithId[]>

  createVolumeComment = async (comment: Omit<VolumeComment, 'createdAt' | 'createdBy' | 'actions'>) =>
    addDoc(
      collection(
        this.db,
        `operations/${comment.operationId}/uploads/${comment.uploadId}/volumes/${comment.volumeId}/comments`
      ),
      {
        ...comment,
        ...this.createdFields(),
        actions: [],
      }
    )

  createSegmentationComment = async (comment: Omit<SegmentationComment, 'createdAt' | 'createdBy' | 'actions'>) =>
    addDoc(
      collection(
        this.db,
        `operations/${comment.operationId}/uploads/${comment.uploadId}/volumes/${comment.volumeId}/segmentations/${comment.segmentationId}/comments`
      ),
      {
        ...comment,
        ...this.createdFields(),
        actions: [],
      }
    )

  createLesionComment = async (comment: Omit<LesionComment, 'createdAt' | 'createdBy' | 'actions'>) =>
    addDoc(
      collection(
        this.db,
          `operations/${comment.operationId}/uploads/${comment.uploadId}/volumes/${comment.volumeId}/comments`
        ),
        {
          ...comment,
          ...this.createdFields(),
          actions: [],
        }
      )

  addCoordinate = async (comment: CommentWithId, coordinate: [number, number, number]) =>
    this.appendCoordinate(comment, coordinate)

  async reply(comment: CommentWithId, text: string) {
    const reply: Fresh<Reply> = {
      type: 'reply',
      ...this.createdFields(),
      text,
    }

    return this.takeAction(comment, reply)
  }

  async resolve(comment: CommentWithId) {
    const resolution: Fresh<CommentAction> = {
      type: 'resolve',
      ...this.createdFields(),
    }

    return this.takeAction(comment, resolution)
  }

  async reopen(comment: CommentWithId) {
    const reopening: Fresh<CommentAction> = {
      type: 'reopen',
      ...this.createdFields(),
    }

    return this.takeAction(comment, reopening)
  }

  delete(comment: CommentWithId) {
    return deleteDoc(this.getDocFromData(comment))
  }

  private async appendCoordinate(comment: CommentWithId, newCoordinate: [number, number,number]) {
    const newKey: number = Object.keys(comment.coordinates).length
    comment.coordinates = {...comment.coordinates,...{[newKey]: newCoordinate}}
    // eslint-able-next-line @typescript-eslint/no-explicit-any
    return updateDoc(this.getDocFromData(comment), comment as any)
  }

  private async takeAction(comment: CommentWithId, action: Fresh<CommentAction>) {
    return updateDoc(this.getDocFromData(comment), {[`actions.${createId(this.db)}`]: action})
  }

  private createdFields = () => ({
    createdAt: serverTimestamp(),
    createdBy: this.auth.currentUser.uid,
  })

  private getDocFromData(comment: CommentWithId) {
    const {operationId, uploadId, volumeId, id} = comment
    const {segmentationId} = comment as SegmentationCommentWithId
    const path = segmentationId
      ? `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/segmentations/${segmentationId}/comments/${id}`
      : `operations/${operationId}/uploads/${uploadId}/volumes/${volumeId}/comments/${id}`

    return doc(this.db, path)  }
}
